/**
 * 261005 E3-P1a — quyết định luồng "sửa khung xương ②" ở FE: xem thử → (so mốc) → (hỏi) → áp. THUẦN, KHÔNG import antd (luật jest của admin-presentation).
 * `SkeletonStage.patch` chỉ thực thi kết quả của `decidePatchFlow`.
 */
import type { PatchPreviewResponse, PatchImpactItem } from '../../../types/adaptation';

/** Cùng cửa sổ ±1 ms của CAS BE (timestamptz µs vs Date ms). */
export const sameInstant = (a: string, b: string): boolean => {
  const x = Date.parse(a), y = Date.parse(b);
  return Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) <= 1;
};

/** `record.modifiedAt` đang hiển thị khác mốc BE vừa đọc ⇒ màn hình đang giữ bản cũ. Thiếu một trong hai ⇒ không kết luận (false). */
export const isViewStale = (baseModifiedAt: string | undefined, recordModifiedAt: string | undefined): boolean =>
  !!baseModifiedAt && !!recordModifiedAt && !sameInstant(baseModifiedAt, recordModifiedAt);

const STATUS_NOTE: Record<string, string> = { FAILED: ' [viết hỏng]' };

/** "Tập 3 (cảnh 2, 5)". */
export const describeImpactItem = (it: PatchImpactItem): string =>
  `Tập ${it.episode} (cảnh ${it.scenes.map((s) => s.index).join(', ')})${STATUS_NOTE[it.status] ?? ''}`;

/** Id treo (nhân vật/bối cảnh) gom từ mọi cảnh — chỉ id thô vì tên đã biến mất khỏi khung xương. */
export const danglingIdsOf = (impact: PatchImpactItem[]): string[] =>
  Array.from(new Set(impact.flatMap((it) => it.scenes.flatMap((s) => [...s.characterIds, ...s.locationIds]))));

export interface PatchConfirmCopy { title: string; lines: string[]; okText: string; cancelText: string }

/**
 * Nội dung hộp xác nhận, hoặc `null` khi không có gì để hỏi.
 * Hỏi khi: có tập đã viết dùng id sắp biến mất, gieo/trích khoảnh khắc đổi làm tập đã viết lệch (`anchorImpact`, 261015 P2b-1), hoặc lượt này BẬT mới cờ "chia tập cần làm lại" (`wasStale=false` ⇒ `preview.episodesStale=true`).
 */
export const buildPatchConfirm = (preview: Pick<PatchPreviewResponse, 'impact' | 'episodesStale'> & { anchorImpact?: PatchPreviewResponse['anchorImpact'] }, wasStale: boolean): PatchConfirmCopy | null => {
  const lines: string[] = [];
  if (preview.impact.length) {
    lines.push(`Các tập đã viết dùng nhân vật/bối cảnh này: ${preview.impact.map(describeImpactItem).join(', ')}.`);
    lines.push(`Id sẽ không còn trong khung xương: ${danglingIdsOf(preview.impact).join(', ')}.`);
    lines.push("Kịch bản giữ nguyên — các tập đó sẽ hiện ở mục \"Cần xem\" của bước ⑤.");
  }
  // 261015 P2b-1: gieo / trích khoảnh khắc đổi làm tập ĐÃ viết lệch (BE dựng chữ). "Tập chưa viết — sẽ vào lời dặn" chỉ là thông tin, không phải ảnh hưởng ⇒ không hỏi.
  // 261015 P2b-2: tập đã viết mà trích mới VẪN chứng minh được (`verdict: quote`) thì không đáng làm người dùng dừng lại.
  const written = (preview.anchorImpact ?? []).filter((x) => x.kind !== 'PLANT_NOT_YET' && x.verdict !== 'quote');
  if (written.length) lines.push(...written.map((x) => x.text));
  if (preview.episodesStale && !wasStale) {
    lines.push('Danh sách tập sẽ cần chia lại ở bước ④ (các tập đã viết giữ nguyên).');
  }
  if (!lines.length) return null;
  return { title: 'Thay đổi này ảnh hưởng phần đã làm', lines, okText: 'Vẫn áp', cancelText: 'Huỷ' };
};

export type PatchFlowDecision =
  | { kind: 'stale-view' }
  | { kind: 'nothing-to-apply' }
  | { kind: 'confirm'; copy: PatchConfirmCopy }
  | { kind: 'apply' };

/** Thứ tự: màn hình cũ ⇒ không áp được gì ⇒ hỏi ⇒ áp. 'Huỷ' ở bước hỏi ⇒ 0 PATCH (do caller). */
export const decidePatchFlow = (
  preview: PatchPreviewResponse, recordModifiedAt: string | undefined, wasStale: boolean,
): PatchFlowDecision => {
  if (isViewStale(preview.baseModifiedAt, recordModifiedAt)) return { kind: 'stale-view' };
  if (!preview.applied.length) return { kind: 'nothing-to-apply' };
  const copy = buildPatchConfirm(preview, wasStale);
  return copy ? { kind: 'confirm', copy } : { kind: 'apply' };
};
